/** Map a forward-pass dep graph's dates onto board-bar spans. Pure, so the
 *  "reflect the apply on the board" behaviour is unit-tested without React.
 *
 *  A committed activity (numeric graph id) patches its `protoSpans` entry by
 *  activity_id; a pending new activity (draft id) patches its `protoNewActs`
 *  row by index. Completed / out-of-scope nodes are skipped (locked history /
 *  not on this board). A node whose start falls off the calendar is skipped. */
import type { DepGraph } from "../../../models";
import type { BarSpan } from "../WbsBoardView/boardLayout";

export interface BoardReflectResult {
  /** committed activity_id → new span */
  spanPatches: Map<number, BarSpan>;
  /** protoNewActs index → new span */
  newPatches: Map<number, BarSpan>;
}

export function computeBoardReflect(
  graph: DepGraph,
  calendarIndexFor: (iso: string | null) => number,
  draftIds: string[],
): BoardReflectResult {
  const spanFromDates = (
    start: string | null,
    finish: string | null,
    fallback: number,
  ): BarSpan | null => {
    const s = calendarIndexFor(start);
    if (s < 0) return null;
    const f = calendarIndexFor(finish);
    const duration = f >= s ? f - s + 1 : Math.max(1, Math.round(fallback));
    return { startIndex: s, durationDays: duration };
  };
  const draftIdx = new Map(draftIds.map((d, i) => [d, i]));
  const spanPatches = new Map<number, BarSpan>();
  const newPatches = new Map<number, BarSpan>();
  for (const n of Object.values(graph.activities)) {
    if (!n.in_scope || n.complete) continue;
    // drive_start/_finish — the remaining-early (CPM) span — not start/finish.
    // For an active activity `start` is the ACTUAL start (locked history); the
    // board plans the remaining work, so its bar sits at the retained start.
    // (For not-started / new bars the two are identical.)
    const span = spanFromDates(n.drive_start, n.drive_finish, n.duration_days);
    if (!span) continue;
    if (/^\d+$/.test(n.id)) {
      spanPatches.set(Number(n.id), span);
    } else {
      const idx = draftIdx.get(n.id);
      if (idx != null) newPatches.set(idx, span);
    }
  }
  return { spanPatches, newPatches };
}

/** Would applying the forward-pass dates actually move any bar? Compares each
 *  in-scope, not-complete activity's computed drive (retained-early) span to
 *  where the board has it now (`boardDates` keyed by the same graph id, with the
 *  same `calendarIndexFor` the apply uses). false ⇒ the board already matches
 *  (the Apply button greys out); true ⇒ there's a real change to apply. An
 *  activity with no placeable start is skipped, exactly as the apply skips it.
 *  Pure, so the green/grey decision is unit-tested without React. */
export function boardDatesDirty(
  graph: DepGraph,
  boardDates: Map<string, { start: string | null; finish: string | null }>,
  calendarIndexFor: (iso: string | null) => number,
): boolean {
  for (const n of Object.values(graph.activities)) {
    if (!n.in_scope || n.complete) continue;
    // Compare the retained-early (drive) span, matching what the apply writes —
    // an active activity's `start` is its actual start, which the board never
    // plans against.
    const gStart = calendarIndexFor(n.drive_start);
    if (gStart < 0) continue;
    const bd = boardDates.get(n.id);
    if (!bd) return true; // on the graph but not placed on the board yet
    if (gStart !== calendarIndexFor(bd.start)) return true;
    if (calendarIndexFor(n.drive_finish) !== calendarIndexFor(bd.finish)) return true;
  }
  return false;
}
